// NEW_FILE_JUSTIFIED: Benefit item card component — handles simple, utilization, and cash benefit variants per .pen design
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/coverage-utils';
import { UtilizationBar } from './UtilizationBar';
import { BenefitInfoPanel } from './BenefitInfoPanel';
import type { IBenefitItemCardProps, IBenefitTagProps } from '@/@types/coverage';

export function BenefitItemCard({ item, utilization, currency, planCoinsurance }: IBenefitItemCardProps) {
  const amountUsed = utilization?.amountUsed ?? 0;
  const visitsUsed = utilization?.visitsUsed ?? 0;
  const annualLimit = item.annualLimit ?? 0;
  const visitLimit = item.visitLimit ?? 0;
  const hasCashBenefit = item.cashBenefitPerUnit !== undefined && item.cashBenefitUnit;

  const tags = buildTags(item);
  const { label: payLabel, isPartial } = getPayLabel(item.planPaysPercent, planCoinsurance);

  return (
    <div className="bg-card px-5 pt-4 pb-6 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium text-foreground">{item.name}</span>
          {item.description && (
            <span className="text-sm text-foreground-muted leading-relaxed">{item.description}</span>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {tags.map((tag, i) => (
                <BenefitTag key={i} label={tag.label} variant={tag.variant} />
              ))}
            </div>
          )}
        </div>
        <span
          className={cn(
            'text-sm font-medium shrink-0 whitespace-nowrap pt-0.5',
            isPartial
              ? 'text-orange-500'
              : 'text-green-400 light:text-green-600'
          )}
        >
          {payLabel}
        </span>
      </div>

      {annualLimit > 0 && (
        <UtilizationBar
          usedValue={amountUsed}
          totalValue={annualLimit}
          usedLabel={`${formatCurrency(amountUsed, currency)} used`}
          remainingLabel={`${formatCurrency(Math.max(0, annualLimit - amountUsed), currency)} remaining`}
          limitLabel={`${formatCurrency(annualLimit, currency)}/yr max`}
        />
      )}

      {visitLimit > 0 && (
        <UtilizationBar
          usedValue={visitsUsed}
          totalValue={visitLimit}
          usedLabel={`${visitsUsed} visits used`}
          remainingLabel={`${Math.max(0, visitLimit - visitsUsed)} visits remaining`}
          limitLabel={`${visitLimit} visits/yr max`}
        />
      )}

      {hasCashBenefit && (
        <>
          <CashBenefitUtilization
            amountUsed={amountUsed}
            perUnit={item.cashBenefitPerUnit ?? 0}
            unit={item.cashBenefitUnit ?? ''}
            maxUnits={item.cashBenefitMaxUnits ?? 0}
            currency={currency}
          />
          <SpendSummary
            amountUsed={amountUsed}
            perUnit={item.cashBenefitPerUnit ?? 0}
            unit={item.cashBenefitUnit ?? ''}
            maxUnits={item.cashBenefitMaxUnits ?? 0}
            currency={currency}
          />
        </>
      )}

      {(item.benefitSpecificNotes ?? item.coverageConditions) && (
        <BenefitInfoPanel
          description={item.benefitSpecificNotes ?? ''}
          exclusionText={item.coverageConditions}
        />
      )}
    </div>
  );
}

function BenefitTag({ label, variant }: IBenefitTagProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
        variant === 'blue'
          ? 'bg-blue-950/30 text-blue-400 light:bg-blue-50 light:text-blue-600'
          : 'bg-muted text-muted-foreground'
      )}
    >
      {label}
    </span>
  );
}

function CashBenefitUtilization({ amountUsed, perUnit, unit, maxUnits, currency }: { // LOCAL_TYPE_JUSTIFIED: inline props for cash benefit utilization bar
  amountUsed: number; perUnit: number; unit: string; maxUnits: number; currency: string;
}) {
  const unitsUsed = perUnit > 0 ? Math.floor(amountUsed / perUnit) : 0;
  const unitsRemaining = Math.max(0, maxUnits - unitsUsed);

  return (
    <UtilizationBar
      usedValue={unitsUsed}
      totalValue={maxUnits}
      usedLabel={`${unitsUsed} ${unit}s used`}
      remainingLabel={`${unitsRemaining} ${unit}s remaining`}
      limitLabel={`${formatCurrency(perUnit, currency)}/${unit} \u00b7 ${maxUnits} ${unit}s max`}
    />
  );
}

function SpendSummary({ amountUsed, perUnit, unit, maxUnits, currency }: { // LOCAL_TYPE_JUSTIFIED: inline props for private helper within BenefitItemCard
  amountUsed: number; perUnit: number; unit: string; maxUnits: number; currency: string;
}) {
  const unitsUsed = perUnit > 0 ? Math.floor(amountUsed / perUnit) : 0;
  const unitsRemaining = Math.max(0, maxUnits - unitsUsed);
  const totalPaid = unitsUsed * perUnit;
  const maxRemaining = unitsRemaining * perUnit;

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-border bg-background-secondary p-3 flex flex-col gap-0.5">
        <span className="text-xs text-foreground-muted">Total paid out</span>
        <span className="text-base font-semibold text-foreground">{formatCurrency(totalPaid, currency)}</span>
        <span className="text-xs text-foreground-muted">{unitsUsed} {unit}s x {formatCurrency(perUnit, currency)}</span>
      </div>
      <div className="rounded-lg border border-border bg-background-secondary p-3 flex flex-col gap-0.5">
        <span className="text-xs text-foreground-muted">Max remaining</span>
        <span className="text-base font-semibold text-foreground">{formatCurrency(maxRemaining, currency)}</span>
        <span className="text-xs text-foreground-muted">{unitsRemaining} {unit}s x {formatCurrency(perUnit, currency)}</span>
      </div>
    </div>
  );
}

function buildTags(item: IBenefitItemCardProps['item']): IBenefitTagProps[] {
  const tags: IBenefitTagProps[] = [];
  if (item.preAuthRequired) {
    tags.push({ label: 'Pre-auth required', variant: 'blue' });
  }
  if (item.cashBenefitPerUnit !== undefined && item.cashBenefitUnit && item.cashBenefitMaxUnits !== undefined) {
    tags.push({
      label: `${item.cashBenefitUnit} (max ${item.cashBenefitMaxUnits})`,
      variant: 'neutral',
    });
  }
  if (item.limitValue) {
    tags.push({ label: item.limitValue, variant: 'neutral' });
  }
  if (item.waitingPeriodMonths !== undefined && item.waitingPeriodMonths > 0) {
    tags.push({ label: `${item.waitingPeriodMonths}mo waiting`, variant: 'neutral' });
  }
  return tags;
}

function getPayLabel(planPaysPercent: number, planCoinsurance: number): { label: string; isPartial: boolean } {
  // Item-level planPaysPercent takes priority over plan-level coinsurance
  const effectivePercent = planPaysPercent ?? planCoinsurance;
  if (effectivePercent === 100) return { label: 'Plan pays 100%', isPartial: false };
  if (effectivePercent > 0) return { label: `Plan pays ${effectivePercent}%`, isPartial: true };
  return { label: 'Included', isPartial: false };
}
